import { ChevronLeft, ChevronRight } from 'lucide-react';
import { type MouseEvent, type PointerEvent, useCallback, useEffect, useMemo, useRef, useState, type WheelEvent } from 'react';

import { ITariff, TariffTypes } from '../../store/tariff.slice';
import { useGetBillingShowcaseQuery, type BillingPackage } from '../../services/billing.api';
import { getTariffCardComponent } from '../tariff/get-tariff-card-component';

interface PlanMock {
  name: string;
  description: string;
  price: {
    monthly: number;
    annual: number;
  };
  basePriceMonthly: number;
  discountPercent: number;
  isFree?: boolean;
  durationDays: number | 'unlimit';
  features: string[];
  popular?: boolean;
  type?: TariffTypes;
  endDate?: Date;
  isActive?: boolean;
}

const planMocks: PlanMock[] = [
  {
    name: 'БАЗОВЫЙ',
    description: 'Базовый доступ к основным сервисам',
    price: {
      monthly: 0,
      annual: 0,
    },
    basePriceMonthly: 0,
    discountPercent: 0,
    isFree: true,
    durationDays: 'unlimit' as const,
    features: [
      'Видеозвонок',
      'ДПДГ онлайн',
      'Цветовой код',
      'Подкинь вопрос',
      'Терапевтический журнал',
      'Метафорические карты',
      'Детектор эмоций',
      'Колесо баланса',
    ],
  },
  {
    name: 'ПРИМА',
    description: 'Тариф на 30 дней',
    price: {
      monthly: 3300,
      annual: 0,
    },
    basePriceMonthly: 3300,
    discountPercent: 0,
    durationDays: 30,
    features: [
      'ИИ Ассистенты (450 минут на 30 дней)',
      'Терапевтический журнал',
      'Колесо баланса',
      'Подкинь вопрос',
      'Видеозвонок',
      'Детектор эмоций',
      'ДПДГ онлайн',
      'Цветовой код',
      'Метафорические карты',
    ],
  },
  {
    name: 'ТЕРЦИЯ',
    description: 'Тариф на 90 дней',
    price: {
      monthly: 7920,
      annual: 0,
    },
    basePriceMonthly: 3300,
    discountPercent: 20,
    durationDays: 90,
    features: [
      'ИИ Ассистенты (1350 минут на 30 дней)',
      'Колесо баланса',
      'Метафорические карты',
      'Видеозвонок',
      'ДПДГ онлайн',
      'Терапевтический журнал',
      'Цветовой код',
      'Подкинь вопрос',
      'Детектор эмоций',
    ],
  },
  {
    name: 'КВИНТА',
    description: 'Тариф на 180 дней',
    price: {
      monthly: 12870,
      annual: 0,
    },
    basePriceMonthly: 3300,
    discountPercent: 35,
    durationDays: 180,
    features: [
      'ИИ Ассистенты (2700 минут на 30 дней)',
      'Видеозвонок',
      'Терапевтический журнал',
      'Метафорические карты',
      'ДПДГ онлайн',
      'Цветовой код',
      'Колесо баланса',
      'Подкинь вопрос',
      'Детектор эмоций',
    ],
  },
  {
    name: 'ОКТАВА',
    description: 'Тариф на 365 дней',
    price: {
      monthly: 21780,
      annual: 0,
    },
    basePriceMonthly: 3300,
    discountPercent: 45,
    durationDays: 365,
    popular: true,
    features: [
      'ИИ Ассистенты (5400 минут на 30 дней)',
      'Видеозвонок',
      'Метафорические карты',
      'ДПДГ онлайн',
      'Терапевтический журнал',
      'Цветовой код',
      'Колесо баланса',
      'Подкинь вопрос',
      'Детектор эмоций',
    ],
  },
];

interface PricingPlansProps {
  onSelectPlan: (tariff: ITariff) => void;
  activePackageCode?: string;
  activeEndAt?: string;
  scheduledPackageCode?: string;
  scheduledStartAt?: string;
}

const interactiveTargetSelector = 'button, a, input, textarea, select, [role="button"]';

const isInteractiveTarget = (target: EventTarget | null) =>
  target instanceof Element && Boolean(target.closest(interactiveTargetSelector));

function hasRemainingActivations(pkg: BillingPackage): boolean {
  const maxActivations = Number(pkg.max_activations);
  const userActivationsCount = Number(pkg.user_activations_count ?? 0);

  if (!Number.isFinite(maxActivations) || maxActivations <= 0) {
    return true;
  }

  return !Number.isFinite(userActivationsCount) || userActivationsCount < maxActivations;
}

export function PricingPlans({
  onSelectPlan,
  activePackageCode,
  activeEndAt,
  scheduledPackageCode,
  scheduledStartAt,
}: PricingPlansProps) {
  const plansContainerRef = useRef<HTMLDivElement>(null);
  const dragStateRef = useRef<{ pointerId: number; startX: number; startScrollLeft: number } | null>(null);
  const suppressCardClickRef = useRef(false);
  const [canScrollPrevious, setCanScrollPrevious] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);
  const [hasScrollablePlans, setHasScrollablePlans] = useState(false);
  const [isDraggingPlans, setIsDraggingPlans] = useState(false);

  const normalizeShowcaseTabName = (name?: string | null) => {
    if (!name) return 'Витрина';

    const normalized = name.trim().toLowerCase().replace(/\s+/g, '-');
    if (normalized === 'по-минутные') {
      return 'По сессиям';
    }

    return name;
  };

  const [activeShowcaseKey, setActiveShowcaseKey] = useState<string | null>(null);
  const billingCycle = 'monthly' as const;
  const { data: showcaseData, isLoading, isError } = useGetBillingShowcaseQuery();
  const activeEndDate = activeEndAt ? new Date(activeEndAt) : undefined;
  const scheduledStartDate = scheduledStartAt ? new Date(scheduledStartAt) : undefined;

  const showcaseTabs = useMemo(() => {
    if (showcaseData?.showcases?.length) {
      return showcaseData.showcases
        .map((showcase, index) => ({
          key:
            showcase.showcase?.code ||
            showcase.showcase?.id ||
            showcase.code ||
            showcase.id ||
            `showcase-${index}`,
          name:
            normalizeShowcaseTabName(showcase.showcase?.name) ||
            showcase.showcase?.code ||
            showcase.showcase?.id ||
            normalizeShowcaseTabName(showcase.name) ||
            showcase.code ||
            showcase.id ||
            'Витрина',
          order: showcase.showcase?.order ?? showcase.order ?? Number.MAX_SAFE_INTEGER,
          initialIndex: index,
          currency: showcase.currency ?? null,
          packages: (showcase.packages ?? []).filter(hasRemainingActivations),
        }))
        .filter((showcase) => showcase.packages.length > 0)
        .sort((a, b) => {
          if (a.order !== b.order) {
            return a.order - b.order;
          }
          return a.initialIndex - b.initialIndex;
        });
    }

    if (showcaseData?.showcase) {
      const packages = (showcaseData.packages ?? []).filter(hasRemainingActivations);
      if (packages.length === 0) {
        return [];
      }

      return [
        {
          key: showcaseData.showcase.code || showcaseData.showcase.id,
          name:
            normalizeShowcaseTabName(showcaseData.showcase.name) ||
            showcaseData.showcase.code ||
            showcaseData.showcase.id,
          currency: showcaseData.currency ?? null,
          packages,
        },
      ];
    }

    return [];
  }, [showcaseData]);

  useEffect(() => {
    if (!showcaseTabs.length) {
      setActiveShowcaseKey(null);
      return;
    }
    const exists = activeShowcaseKey
      ? showcaseTabs.some((showcase) => showcase.key === activeShowcaseKey)
      : false;
    if (!exists) {
      setActiveShowcaseKey(showcaseTabs[0].key);
    }
  }, [activeShowcaseKey, showcaseTabs]);

  const tariffs = useMemo<ITariff[]>(() => {
    const selectedShowcase = showcaseTabs.find((showcase) => showcase.key === activeShowcaseKey) ?? null;
    const sourcePackages = selectedShowcase?.packages?.length ? selectedShowcase.packages : null;
    const currency = selectedShowcase?.currency ?? showcaseData?.currency ?? 'RUB';

    if (sourcePackages) {
      return [...sourcePackages]
        .sort((a, b) => a.display_order - b.display_order)
        .map((pkg, index) => {
          const price = Number(pkg.price_amount ?? 0);
          const basePrice = pkg.base_price ? Number(pkg.base_price) : price;
          const baseDuration = pkg.base_duration ? Number(pkg.base_duration) : 30;
          const baseDiscount = pkg.base_discount ? Number(pkg.base_discount) : 0;
          const isFree = price === 0;
          const durationDays = pkg.validity_days ?? 'unlimit';
          const unitShort = (unit?: string | null) => {
            if (!unit) return '';
            if (unit.toLowerCase().includes('минут')) return 'мин';
            return unit;
          };
          const formatLimit = (value: number) =>
            new Intl.NumberFormat('ru-RU', { maximumFractionDigits: 0 }).format(value);
          const buildFeatureEntry = (feature: (typeof pkg.features)[number], withLimitLabel: boolean) => {
            const limitValue = Number(feature.limit ?? 0);
            const hasLimit = Number.isFinite(limitValue) && limitValue > 0;
            const title = feature.title;
            const displayTitle =
              withLimitLabel && hasLimit
                ? `${title} (${formatLimit(limitValue)} ${unitShort(feature.unit)})`
                : title;

            return {
                title,
              displayTitle,
              limit: Number.isFinite(limitValue) ? limitValue : null,
            };
          };

          const toolEntries = [
            ...(pkg.metered_features ?? []).map((feature) => buildFeatureEntry(feature, true)),
            ...(pkg.features ?? []).map((feature) => buildFeatureEntry(feature, false)),
          ];

          const featureMap = new Map<string, (typeof toolEntries)[number]>();
          toolEntries.forEach((entry) => {
            const existing = featureMap.get(entry.title);
            if (!existing) {
              featureMap.set(entry.title, entry);
              return;
            }

            const existingLimit = existing.limit ?? null;
            const entryLimit = entry.limit ?? null;
            const existingHasLimit = typeof existingLimit === 'number' && existingLimit > 0;
            const entryHasLimit = typeof entryLimit === 'number' && entryLimit > 0;

            if (entryHasLimit && !existingHasLimit) {
              featureMap.set(entry.title, entry);
            }
          });
          const allFeatures = Array.from(featureMap.values());

          return {
            id: pkg.code || `package-${index + 1}`,
            name: pkg.name,
            type: isFree ? TariffTypes.FREE : TariffTypes.REGULAR,
            duration_days: durationDays,
            description_1: pkg.description1 ?? '',
            description_2: pkg.description2 ?? '',
            package_type: pkg.type,
            start_date: new Date(),
            end_date: new Date(),
            price,
            currency_symbol: currency === 'RUB' ? '₽' : currency,
            currency_code: currency,
            purchase_once: true,
            discount: 0,
            discountDurationDays: 0,
            is_bonus_payment_available: pkg.can_pay_with_bonus,
            bonus_payment_limit_percent: pkg.bonus_payment_limit,
            is_popular: pkg.is_popular,
            auto_renew: false,
            tool_names: [],
            tool_labels: [],
            tools_data: allFeatures.map((feature) => ({
              name: feature.displayTitle,
              tool_name: feature.displayTitle,
              tool_description: null,
              limit: feature.limit,
            })),
            status: 0,
            created_at: new Date(),
            base_duration: baseDuration,
            base_price: basePrice,
            base_discount: baseDiscount,
            updated_at: new Date(),
            sort: pkg.display_order,
          };
        });
    }

    // Backend responded but no showcases/packages available: do not fallback to mocks.
    if (showcaseData) {
      return [];
    }

    return planMocks.map((plan, index) => {
      const currentPrice = plan.price.monthly;
      const basePrice = plan.basePriceMonthly ?? plan.price.monthly;
      const baseDiscount = plan.discountPercent ?? 0;
      const isFree = plan.isFree || currentPrice === 0;

      return {
        id: `mock-${index + 1}`,
        name: plan.name,
        type: plan.type ?? (isFree ? TariffTypes.FREE : TariffTypes.REGULAR),
        duration_days: plan.durationDays ?? 30,
        description_1: plan.description,
        description_2: '',
        package_type: 'base',
        start_date: new Date(),
        end_date: plan.endDate ?? new Date(),
        price: currentPrice,
        currency_symbol: '₽',
        currency_code: 'RUB',
        purchase_once: false,
        discount: 0,
        discountDurationDays: 0,
        is_bonus_payment_available: true,
        bonus_payment_limit_percent: 30,
        is_popular: Boolean(plan.popular),
        auto_renew: true,
        tool_names: [],
        tool_labels: [],
        tools_data: plan.features.map((feature) => ({
          name: feature,
          tool_name: feature,
          tool_description: null,
          limit: null,
        })),
        status: 0,
        created_at: new Date(),
        base_duration: 30,
        base_price: basePrice,
        base_discount: baseDiscount,
        updated_at: new Date(),
        sort: index + 1,
      };
    });
  }, [activeShowcaseKey, showcaseData, showcaseTabs]);
  const tariffToolsMaxCount = useMemo(
    () => Math.max(...tariffs.map((tariff) => tariff.tools_data.length), 0),
    [tariffs],
  );

  const updateScrollControls = useCallback(() => {
    const container = plansContainerRef.current;
    if (!container) {
      return;
    }

    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    setHasScrollablePlans(maxScrollLeft > 1);
    setCanScrollPrevious(container.scrollLeft > 1);
    setCanScrollNext(container.scrollLeft < maxScrollLeft - 1);
  }, []);

  const scrollPlans = useCallback(
    (direction: 'previous' | 'next') => {
      const container = plansContainerRef.current;
      if (!container) {
        return;
      }

      container.scrollBy({
        left: direction === 'previous' ? -container.clientWidth : container.clientWidth,
        behavior: 'smooth',
      });
    },
    [],
  );

  const handlePlansWheel = useCallback((event: WheelEvent<HTMLDivElement>) => {
    const container = plansContainerRef.current;
    if (!container) {
      return;
    }

    const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
    const maxScrollLeft = container.scrollWidth - container.clientWidth;
    const nextScrollLeft = Math.min(Math.max(container.scrollLeft + delta, 0), maxScrollLeft);

    if (nextScrollLeft === container.scrollLeft) {
      return;
    }

    event.preventDefault();
    container.scrollTo({ left: nextScrollLeft });
  }, []);

  const handlePlansPointerDown = useCallback((event: PointerEvent<HTMLDivElement>) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) {
      return;
    }

    if (isInteractiveTarget(event.target)) {
      return;
    }

    const container = event.currentTarget;
    dragStateRef.current = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: container.scrollLeft,
    };
    suppressCardClickRef.current = false;
    setIsDraggingPlans(true);
    container.setPointerCapture(event.pointerId);
  }, []);

  const handlePlansPointerMove = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    const distance = event.clientX - dragState.startX;
    if (Math.abs(distance) > 4) {
      suppressCardClickRef.current = true;
    }
    event.currentTarget.scrollTo({ left: dragState.startScrollLeft - distance });
  }, []);

  const handlePlansPointerEnd = useCallback((event: PointerEvent<HTMLDivElement>) => {
    const dragState = dragStateRef.current;
    if (!dragState || dragState.pointerId !== event.pointerId) {
      return;
    }

    dragStateRef.current = null;
    setIsDraggingPlans(false);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  }, []);

  const handlePlansClickCapture = useCallback((event: MouseEvent<HTMLDivElement>) => {
    if (!suppressCardClickRef.current) {
      return;
    }

    suppressCardClickRef.current = false;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  useEffect(() => {
    const container = plansContainerRef.current;
    if (!container) {
      return;
    }

    updateScrollControls();
    container.addEventListener('scroll', updateScrollControls, { passive: true });
    const resizeObserver = new ResizeObserver(updateScrollControls);
    resizeObserver.observe(container);

    return () => {
      container.removeEventListener('scroll', updateScrollControls);
      resizeObserver.disconnect();
    };
  }, [tariffs.length, updateScrollControls]);

  return (
    <div className="space-y-12">
      <div className="text-center">
        {showcaseTabs.length > 1 && (
          <div className="inline-flex items-center rounded-[12px] border border-[#E6E6E6] bg-white p-1 mb-2">
            {showcaseTabs.map((showcase) => (
              <button
                key={showcase.key}
                type="button"
                className={`px-4 py-2 rounded-[10px] text-sm ${
                  activeShowcaseKey === showcase.key
                    ? 'bg-blackBase text-white'
                    : 'text-grey2 hover:text-blackBase'
                }`}
                onClick={() => {
                  setActiveShowcaseKey(showcase.key);
                }}
              >
                {showcase.name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-4">
        <div
          ref={plansContainerRef}
          className={`-mx-5 flex ${isDraggingPlans ? 'snap-none' : 'snap-x snap-mandatory'} gap-4 overflow-x-auto overscroll-x-contain pb-4 pl-4 pr-5 no-scrollbar sm:mx-0 sm:pl-4 sm:pr-0 md:items-stretch md:cursor-grab md:active:cursor-grabbing ${hasScrollablePlans ? '' : 'md:justify-center'}`}
          aria-label="Тарифы"
          onWheel={handlePlansWheel}
          onPointerDown={handlePlansPointerDown}
          onPointerMove={handlePlansPointerMove}
          onPointerUp={handlePlansPointerEnd}
          onPointerCancel={handlePlansPointerEnd}
          onClickCapture={handlePlansClickCapture}
        >
        {isLoading &&
          Array.from({ length: 3 }).map((_, index) => (
            <div
              key={`tariff-skeleton-${index}`}
              className={`h-[456px] w-[272px] min-w-[272px] shrink-0 snap-start animate-pulse rounded-3xl border border-[#DEC05E] bg-white p-6 ${
                index === 0 ? 'scroll-ml-4' : ''
              }`}
            >
              <div className="h-4 w-24 bg-greyWithOpacity30 rounded mb-4 mx-auto" />
              <div className="h-6 w-40 bg-greyWithOpacity30 rounded mb-4 mx-auto" />
              <div className="h-8 w-28 bg-greyWithOpacity30 rounded mb-6 mx-auto" />
              <div className="h-3 w-32 bg-greyWithOpacity30 rounded mb-4 mx-auto" />
              <div className="h-8 w-full bg-greyWithOpacity30 rounded mb-6" />
              <div className="space-y-3">
                {Array.from({ length: 6 }).map((_, itemIndex) => (
                  <div key={`feature-skeleton-${index}-${itemIndex}`} className="h-3 w-full bg-greyWithOpacity30 rounded" />
                ))}
              </div>
            </div>
          ))}
        {!isLoading && isError && (
          <div className="w-full rounded-[24px] border border-[#EFEFEF] bg-white p-6 text-center text-grey1">
            Не удалось загрузить тарифы. Попробуйте обновить страницу.
          </div>
        )}
        {!isLoading &&
          !isError &&
          tariffs.length === 0 && (
            <div className="w-full rounded-[24px] border border-[#EFEFEF] bg-white p-6 text-center text-grey1">
              Для выбранного режима тарифы не найдены.
            </div>
          )}
        {!isLoading &&
          !isError &&
          tariffs.map((tariff, index) => {
            const TariffCardComponent = getTariffCardComponent(tariff.package_type);
            const isActive = activePackageCode ? tariff.id === activePackageCode : false;
            const isScheduled = scheduledPackageCode ? tariff.id === scheduledPackageCode : false;
            const selectionDisabled = Boolean(scheduledPackageCode);
            const startEndDate = isScheduled ? scheduledStartDate : isActive ? activeEndDate : undefined;

            return (
              <div
                key={tariff.id}
                className={`w-[272px] min-w-[272px] shrink-0 snap-start ${index === 0 ? 'scroll-ml-4' : ''}`}
              >
                <TariffCardComponent
                  tariff={tariff}
                  displayMode="full"
                  tariffToolsMaxCount={tariffToolsMaxCount}
                  onSelectClick={() => {
                    if (!selectionDisabled) {
                      onSelectPlan(tariff);
                    }
                  }}
                  active={isActive}
                  scheduled={isScheduled}
                  selectDisabled={selectionDisabled}
                  hideRenew={selectionDisabled}
                  start_end_date={startEndDate}
                />
              </div>
            );
          })}
          <div aria-hidden="true" className="hidden w-4 shrink-0 sm:block" />
        </div>
        {hasScrollablePlans && <div className="hidden justify-center gap-2 md:flex">
          <button
            type="button"
            aria-label="Показать предыдущие тарифы"
            title="Предыдущие тарифы"
            disabled={!canScrollPrevious}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E6E6E6] bg-white text-blackBase transition-colors hover:border-blackBase disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => scrollPlans('previous')}
          >
            <ChevronLeft aria-hidden className="h-5 w-5" />
          </button>
          <button
            type="button"
            aria-label="Показать следующие тарифы"
            title="Следующие тарифы"
            disabled={!canScrollNext}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-[#E6E6E6] bg-white text-blackBase transition-colors hover:border-blackBase disabled:cursor-not-allowed disabled:opacity-40"
            onClick={() => scrollPlans('next')}
          >
            <ChevronRight aria-hidden className="h-5 w-5" />
          </button>
        </div>}
      </div>

      <div className="rounded-[24px] border border-[#EFEFEF] bg-white p-6">
        <h2 className="font-comfortaa text-center mb-6">Частые вопросы</h2>
        <div className="space-y-6 text-sm text-grey1">
          <div>
            <h4 className="mb-2 text-blackBase">Можно ли сменить тариф позже?</h4>
            <p>
              Да, вы можете перейти на другой тариф в любой момент. Изменения вступят в силу в следующем платежном периоде.
            </p>
          </div>
          <div>
            <h4 className="mb-2 text-blackBase">Что если я превысил лимит минут?</h4>
            <p>
              Можно перейти на тариф выше.
            </p>
          </div>
          <div>
            <h4 className="mb-2 text-blackBase">Есть ли пробный период?</h4>
            <p>Пробного периода у тарифов нет.</p>
          </div>
          <div>
            <h4 className="mb-2 text-blackBase">Можно ли отменить тариф?</h4>
            <p>Отмены тарифа нет — подписка действует до следующего платежного периода.</p>
          </div>
          <div>
            <h4 className="mb-2 text-blackBase">Вопросы по тарифам</h4>
            <p>Пишите на <a className="text-redBase" href="mailto:info@zingizi.pro">info@zingizi.pro</a>.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
